import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import UsageProgressBar from "../../components/UsageProgressBar";
import { currentPeriod, fetchPlatformTenants, fetchTenantUsageSummary } from "../../lib/opsGraphql";

function sortByConsumption(tenants, usageByTenant) {
  return [...tenants].sort((a, b) => {
    const ua = usageByTenant[a.tenant_id];
    const ub = usageByTenant[b.tenant_id];
    const scoreA = ua?.usage_pct ?? ua?.documents_processed ?? 0;
    const scoreB = ub?.usage_pct ?? ub?.documents_processed ?? 0;
    return scoreB - scoreA;
  });
}

export default function OpsHome() {
  const [tenants, setTenants] = useState([]);
  const [usageByTenant, setUsageByTenant] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const period = currentPeriod();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const rows = await fetchPlatformTenants({ status: "active" });
        if (cancelled) return;
        setTenants(rows);
        const usageEntries = await Promise.all(
          rows.map(async (t) => {
            try {
              const usage = await fetchTenantUsageSummary(t.tenant_id, period);
              return [t.tenant_id, usage];
            } catch {
              return [t.tenant_id, null];
            }
          })
        );
        if (!cancelled) {
          setUsageByTenant(Object.fromEntries(usageEntries));
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Error al cargar resumen");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [period]);

  const sorted = useMemo(
    () => sortByConsumption(tenants, usageByTenant),
    [tenants, usageByTenant]
  );

  const exceeded = sorted.filter((t) => usageByTenant[t.tenant_id]?.plan_exceeded);

  return (
    <>
      {error && <p className="ops-error">{error}</p>}
      <div className="ops-card">
        <h2>Consumo por tenant ({period})</h2>
        <p className="ops-subtitle" style={{ marginBottom: 16 }}>
          Tenants activos: <strong>{tenants.length}</strong> · Excedidos:{" "}
          <strong>{exceeded.length}</strong>
        </p>

        {loading ? (
          <p className="ops-loading">Cargando consumo…</p>
        ) : sorted.length === 0 ? (
          <p>No hay tenants activos.</p>
        ) : (
          <table className="ops-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Documentos procesados</th>
                <th>Tokens fallback</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((t) => {
                const u = usageByTenant[t.tenant_id];
                if (!u) {
                  return (
                    <tr key={t.tenant_id}>
                      <td>
                        <Link to={`/tenants/${t.tenant_id}`}>{t.name}</Link>
                        <br />
                        <small>{t.tenant_id}</small>
                      </td>
                      <td colSpan={2}>—</td>
                      <td />
                    </tr>
                  );
                }
                const fallbackLimit =
                  u.fallback_tokens_included && u.fallback_tokens_included > 0
                    ? u.fallback_tokens_included
                    : null;

                return (
                  <tr key={t.tenant_id}>
                    <td>
                      <Link to={`/tenants/${t.tenant_id}`}>
                        <strong>{t.name}</strong>
                      </Link>
                      <br />
                      <small>{t.tenant_id}</small>
                    </td>
                    <td style={{ minWidth: 220 }}>
                      <UsageProgressBar
                        used={u.documents_processed}
                        limit={u.documents_limit}
                      />
                    </td>
                    <td style={{ minWidth: 220 }}>
                      <UsageProgressBar
                        used={u.fallback_tokens_used}
                        limit={fallbackLimit}
                      />
                    </td>
                    <td>
                      <Link to={`/tenants/${t.tenant_id}/consumo`} className="ops-btn secondary">
                        Detalle
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
